/**
 * Handle chunk loading failures that occur after new deployments.
 * When a new version is deployed, old chunk files are removed.
 * Users with cached HTML may try to load non-existent chunks.
 * This utility detects these failures and triggers a page reload.
 */

const RELOAD_KEY = 'chunk_error_reload';
const RELOAD_COOLDOWN = 10000; // 10 seconds between reloads

function shouldReload(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();
  return !lastReload || now - parseInt(lastReload) > RELOAD_COOLDOWN;
}

function markReload(): void {
  sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
}

function isChunkLoadError(error: Error | string): boolean {
  const message = typeof error === 'string' ? error : error.message;
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('ChunkLoadError') ||
    // Vite-specific error patterns
    message.includes('Unable to preload CSS') ||
    message.includes('Failed to load module script')
  );
}

export function handleChunkError(error: Error): void {
  if (isChunkLoadError(error) && shouldReload()) {
    console.debug('[ChunkErrorHandler] Detected chunk load failure, reloading page...');
    markReload();
    window.location.reload();
  }
}

/**
 * Initialize global chunk error handling.
 * Call this once at app startup.
 */
export function initChunkErrorHandler(): void {
  // Handle unhandled promise rejections (dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (error instanceof Error && isChunkLoadError(error)) {
      event.preventDefault();
      handleChunkError(error);
    }
  });

  // Handle regular errors
  window.addEventListener('error', (event) => {
    if (event.message && isChunkLoadError({ message: event.message } as Error)) {
      event.preventDefault();
      handleChunkError({ message: event.message } as Error);
    }
  });

  console.debug('[ChunkErrorHandler] Initialized global chunk error handling');
}

/**
 * Clear the reload marker (useful after successful page load)
 */
export function clearChunkErrorState(): void {
  sessionStorage.removeItem(RELOAD_KEY);
}
